import type {
  TaskAssignmentResponse,
  TaskAssignmentSimpleResponse,
  TaskAssignmentStateEnum,
  TaskBlendedResponse,
} from 'src/api/ProactApiClient';
import {readSnapshotData} from 'src/offline/offlineQuery';
import type {EnqueueApplyLocalContext} from 'src/offline/outbox';
import type {PendingSync} from 'src/offline/pendingSync';
import {collectPendingRefs, enqueuePendingUploads} from 'src/offline/pendingUploads';
import {putSnapshotPreservingCachedAt as putSnapshot} from 'src/offline/snapshotWrites';
import type {IStateMachine} from 'src/types/StateMachine';
import type {OfflineActionCreateRequest} from './queuedTransition';
import {
  taskActionKey,
  taskAssignmentKey,
  tasksAssignmentsListKey,
  tasksAssignmentStateMachineKey,
} from './snapshotKeys';

export interface TransitionApplierArgs {
  assignmentId: number;
  transition: string;
}

// The cached assignment machine names the target state; without it the row
// still flips to pending so the user sees their action was recorded.
const targetState = async (
  sessionId: string,
  transition: string,
  from: TaskAssignmentStateEnum
): Promise<TaskAssignmentStateEnum | undefined> => {
  const machine = await readSnapshotData<IStateMachine>(
    sessionId,
    tasksAssignmentStateMachineKey
  );
  const match = machine?.transitions.find(
    candidate => candidate.name === transition && candidate.from.includes(from)
  );
  return match?.to as TaskAssignmentStateEnum | undefined;
};

/** Moves the detail, list and Action snapshot rows to the queued target state. */
export const applyTaskTransition = async (
  args: TransitionApplierArgs,
  ctx: EnqueueApplyLocalContext
): Promise<void> => {
  const assignment = await readSnapshotData<
    PendingSync<TaskAssignmentResponse>
  >(ctx.sessionId, taskAssignmentKey(args.assignmentId));
  if (!assignment) return;

  const state =
    (await targetState(ctx.sessionId, args.transition, assignment.state)) ??
    assignment.state;
  const updated = {...assignment, state, pendingSync: true};
  await putSnapshot(
    ctx.sessionId,
    taskAssignmentKey(args.assignmentId),
    updated
  );

  const rows = await readSnapshotData<PendingSync<TaskAssignmentSimpleResponse>[]>(
    ctx.sessionId,
    tasksAssignmentsListKey
  );
  if (rows) {
    await putSnapshot(
      ctx.sessionId,
      tasksAssignmentsListKey,
      rows.map(row =>
        row.id === args.assignmentId ? {...row, state, pendingSync: true} : row
      )
    );
  }

  const action = await readSnapshotData<TaskBlendedResponse>(
    ctx.sessionId,
    taskActionKey(assignment.taskId)
  );
  if (action?.assignment?.id === args.assignmentId) {
    await putSnapshot(ctx.sessionId, taskActionKey(assignment.taskId), {
      ...action,
      assignment: updated,
    });
  }
};

/** Queues the create's offline-captured inline attachments in its transaction. */
export const applyActionCreate = (
  request: OfflineActionCreateRequest,
  ctx: EnqueueApplyLocalContext
): Promise<void> =>
  enqueuePendingUploads(ctx.sessionId, [request.inlineAttachments]);

export const actionUploadRefs = (payload: unknown): ReturnType<typeof collectPendingRefs> =>
  collectPendingRefs([(payload as OfflineActionCreateRequest).inlineAttachments]);
